import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, RefreshCw, Calendar, IndianRupee } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const paymentBadge = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
};

const statusBadge = {
  confirmed: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
};

export const MyRegistrationsSection = ({ token }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const res = await axios.get(`${API}/programs/my-registrations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRegistrations(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchRegistrations();
  }, [token]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-400" />
        <p className="text-sm text-slate-500 mt-2">Loading your registrations...</p>
      </div>
    );
  }

  return (
    <div data-testid="my-registrations-section">
      <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">My Registrations</h3>
      {registrations.length === 0 ? (
        <div className="text-center py-12 bg-[#F8FAFC] rounded-sm border border-slate-100" data-testid="no-registrations">
          <GraduationCap className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">You have no program registrations yet.</p>
          <p className="text-xs text-slate-400 mt-1">Registrations for examinations and programs will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {registrations.map((reg) => (
            <Card key={reg.id} className="imcac-card" data-testid={`registration-card-${reg.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className="text-xs rounded-sm font-mono">{reg.program_code}</Badge>
                      <Badge className={`text-xs rounded-sm capitalize ${statusBadge[reg.status] || 'bg-slate-100 text-slate-600'}`}>
                        {reg.status}
                      </Badge>
                    </div>
                    <p className="font-semibold text-[#1B2A4A] text-sm">{reg.program_name}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(reg.created_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <IndianRupee className="h-3 w-3" />
                        {(reg.fee_amount || 0).toLocaleString()}
                      </span>
                      <span className="capitalize">{reg.program_category}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Payment</p>
                    <Badge className={`text-xs rounded-sm capitalize ${paymentBadge[reg.payment_status] || 'bg-slate-100 text-slate-600'}`} data-testid={`payment-status-${reg.id}`}>
                      {reg.payment_status || 'pending'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
